"use client";

import { useMemo } from "react";
import { useSnapshot } from "valtio";
import { useGoalDetail } from "@/src/hooks/goal";
import { rootStore } from "@/src/store";
import {
  isEstablishedGoal,
  mapGoalToDiagnosisUnlockPayload,
} from "./mapGoalToDiagnosisUnlockPayload";
import type { DiagnosisUnlockModalPayload } from "./types";

export type UseDiagnosisUnlockGoalPayloadParams = {
  fetchGoalDetail: boolean;
  goalId?: string;
  staticPayload?: DiagnosisUnlockModalPayload;
  /** 进入 step2 后才请求 */
  enabled: boolean;
};

export type UseDiagnosisUnlockGoalPayloadResult = {
  payload: DiagnosisUnlockModalPayload | undefined;
  isLoading: boolean;
  isError: boolean;
  hasEstablishedGoal: boolean;
};

export function useDiagnosisUnlockGoalPayload({
  fetchGoalDetail,
  goalId,
  staticPayload,
  enabled,
}: UseDiagnosisUnlockGoalPayloadParams): UseDiagnosisUnlockGoalPayloadResult {
  const loginSnapshot = useSnapshot(rootStore.login);
  const isLoggedIn = loginSnapshot.isLoggedIn === true;

  const shouldFetch = fetchGoalDetail && Boolean(goalId) && enabled;

  const { data, isLoading, notFound, isError } = useGoalDetail(
    goalId ?? "",
    shouldFetch && isLoggedIn,
  );

  const mappedPayload = useMemo(() => {
    if (!fetchGoalDetail || !data) {
      return undefined;
    }
    return mapGoalToDiagnosisUnlockPayload(data) ?? undefined;
  }, [data, fetchGoalDetail]);

  if (!fetchGoalDetail) {
    return {
      payload: staticPayload,
      isLoading: false,
      isError: false,
      hasEstablishedGoal: false,
    };
  }

  if (shouldFetch && !isLoggedIn) {
    return {
      payload: undefined,
      isLoading: false,
      isError: true,
      hasEstablishedGoal: false,
    };
  }

  const fetchPending = shouldFetch && isLoading;
  const fetchFailed =
    shouldFetch &&
    (isError || notFound || (!fetchPending && data != null && !mappedPayload));

  return {
    payload: mappedPayload,
    isLoading: fetchPending,
    isError: fetchFailed,
    hasEstablishedGoal: data ? isEstablishedGoal(data) : false,
  };
}
